


































































import { computed, defineComponent, ref, set } from "@vue/composition-api";
import useAuthUser from "@/hooks/useAuthUser";
import useUserVerification from "@/hooks/useUserVerification";
import providers from "@/lib/api";
import config from "@/config";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseStepper from "@/components/BaseStepper";
import PageTitle from "@/components/PageTitle";
import PayoutForm from "@/components/PayoutForm";
import PayeeInfoButton from "./PayeeInfoButton.vue";
import PersonalIdForm from "./PersonalIdForm.vue";
import PersonalInfoForm from "./PersonalInfoForm.vue";
import PersonalLocalization from "./PersonalLocalization.vue";
import PromoteView from "./PromoteView";
import SafeForWorkSetup from "./SafeForWorkSetup.vue";

interface StepData {
  step: number;
}

export default defineComponent({
  name: "PayeeInfoSetupView",
  components: {
    PayoutForm,
    PersonalIdForm,
    PersonalInfoForm,
    PayeeInfoButton,
    BaseButton,
    BaseIcon,
    PageTitle,
    BaseStepper,
    PromoteView,
    PersonalLocalization,
    SafeForWorkSetup,
  },
  setup(_, { emit }) {
    const { authUserStrict, updateUser } = useAuthUser();
    const { start, findMyStatus } = useUserVerification();
    const submits = ref<Record<string, () => Promise<any>>>({});
    const showPromote = ref(true);
    const valid = ref(false);
    const loading = ref(false);
    const sendingData = ref(false);
    const current = ref(1);
    const showLocalization = computed(() => !config.featureHideLocalization);
    const showSafeForWorkSetup = computed(() => config.featureNSFW);
    const currentStep = (step: number) => {
      return "step-" + step;
    };
    const stepsArray = computed(() => {
      return [
        "personalInfo",
        ...(showLocalization.value ? ["personalLocalization"] : []),
        ...(showSafeForWorkSetup.value ? ["safeForWorkStatus"] : []),
        "sensitiveAgreement",
        "payeeInfo",
      ];
    });

    const steps = computed(() => {
      return stepsArray.value.reduce<Record<string, StepData | null>>((acc, item, index) => {
        acc[item] = {
          step: index + 1,
        };
        return acc;
      }, {});
    });

    const handleTitleClick = () => {
      if (current.value === 1) {
        showPromote.value = true;
      } else {
        return current.value--;
      }
    };

    const startUserVerification = async () => {
      try {
        sendingData.value = true;
        await start(authUserStrict.value.id);
        emit("form:finishStep");
        await findMyStatus();
        const { role, authorities } = await providers.UserProvider.getMyAuthorities();
        updateUser({ ...authUserStrict.value, role }, authorities);
      } finally {
        sendingData.value = false;
      }
    };

    const handleButtonClick = async (hasNext: boolean, step: number) => {
      for (let key in steps.value) {
        if (steps.value[key]?.step !== step) {
          continue;
        }
        try {
          sendingData.value = true;
          await submits.value[key]?.();
        } catch (e) {
          /* eslint-disable no-console */
          console.error(e);
          return;
        } finally {
          sendingData.value = false;
        }
        if (hasNext) {
          loading.value = false;
          current.value = current.value + 1;
        } else {
          await startUserVerification();
        }
      }
    };
    const setCb = (stepName: string, cb: Promise<any>) => {
      set(submits.value, stepName, cb);
    };

    return {
      showPromote,
      loading,
      valid,
      sendingData,
      current,
      steps,
      showLocalization,
      showSafeForWorkSetup,
      currentStep,
      setCb,
      handleTitleClick,
      handleButtonClick,
    };
  },
});
